import React from 'react'

import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { smallWidgetTypes } from '../Dashboard/types'
import { SmallWidget } from '../Dashboard/widgetsComponents'

import { CashFlowStatsResponseType } from './types'

type Props = {
  currency?: string | null
  balance: number
  balanceLoading?: boolean
  stats: CashFlowStatsResponseType
  widgetsLoading?: boolean
}

export const CashFlowSummaryWidgets: React.FC<Props> = ({
  currency,
  balance,
  balanceLoading = false,
  stats,
  widgetsLoading = false,
}) => {
  const { t } = useTranslation()

  return (
    <Box
      display="grid"
      gridTemplateColumns={{
        xs: 'repeat(2, minmax(0, 1fr))',
        sm: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(5, minmax(0, 1fr))',
      }}
      gap={2}
      mb={2}
    >
      <SmallWidget
        isLoading={balanceLoading}
        label={t('tradingAccountsPage.accountCard.balance', 'Balance')}
        mainInfo={balance}
        currency={currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t(
          'cashFlowPage.balanceExplanation',
          'Calculated balance based on imported cash flows and realized net profit from closed trades.',
        )}
      />
      <SmallWidget
        isLoading={widgetsLoading}
        label={t('netProfit', 'Net Profit')}
        mainInfo={stats.netProfit}
        currency={currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t('netProfitExplanation')}
      />
      <SmallWidget
        isLoading={widgetsLoading}
        label={t('cashFlowPage.type.deposit', 'Deposit')}
        mainInfo={stats.deposits}
        currency={currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t(
          'cashFlowPage.depositsExplanation',
          'Sum of all positive non-credit cash flow rows, including deposits and positive adjustments.',
        )}
      />
      <SmallWidget
        isLoading={widgetsLoading}
        label={t('cashFlowPage.type.withdrawal', 'Withdrawal')}
        mainInfo={stats.withdrawals}
        currency={currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t(
          'cashFlowPage.withdrawalsExplanation',
          'Sum of all negative non-credit cash flow rows, including withdrawals and negative adjustments.',
        )}
      />
      <SmallWidget
        isLoading={widgetsLoading}
        label={t('cashFlowPage.type.credit', 'Credit')}
        mainInfo={stats.credits}
        currency={currency ?? ''}
        type={smallWidgetTypes.money}
        explanation={t(
          'cashFlowPage.creditsExplanation',
          'Sum of broker credit rows. Credits are shown separately and are excluded from deposits, withdrawals, and calculated balance.',
        )}
      />
    </Box>
  )
}
